import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { ErrorResponse, HttpStatusCode, PaginatedResponse } from '../../api';
import {
  deleteCommentOnStory,
  insertCommentOnStory,
  insertReplyToCommentOnStory,
} from '../comments';
import {
  deleteStory,
  fetchAllStories,
  fetchFollowingStories,
  fetchFullStory,
  fetchFullStoryForEdit,
  fetchMyStories,
  fetchMyStoryStats,
  fetchStoriesByAuthor,
  fetchStoriesByTopic,
  fetchStoryTags,
  fetchStoryTopics,
  fetchSuggestedStories,
  insertFeaturedStory,
  insertStory,
  likeStory,
  moderatorDeleteStory,
  moderatorUpdateStory,
  updateFeaturedContent,
  updateFeaturedStory,
} from './actions';
import { InsertStoryDto } from './dtos';
import {
  MyStoriesStats,
  MyStoryState,
  Story,
  StorySettings,
  Tag,
  Topic,
} from './model';

export interface StoriesState {
  allStories: PaginatedResponse<Story[]>;
  followingStories: PaginatedResponse<Story[]>;
  myStories: PaginatedResponse<Story[]>;
  suggestedStories: PaginatedResponse<Story[]>;
  storiesByAuthor: PaginatedResponse<Story[]>;
  storiesByTopic: PaginatedResponse<Story[]>;
  storiesByAuthorDisplayName?: string;
  titleTopicName?: string;
  storyTags: Tag[];
  fullStory?: Story;
  storyTopics: Topic[];
  editedStory?: Story;
  currentStorySettings?: StorySettings;
  publishInProgress: boolean;
  loadingStoriesByAuthor: boolean;
  loadingStoriesByTopic: boolean;
  loadingAllStories: boolean;
  loadingSuggestedStories: boolean;
  loadingFollowingStories: boolean;
  loadingMyStories: boolean;
  loadingStoryTags: boolean;
  loadingStoryTopics: boolean;
  myStoryStats?: MyStoriesStats;
  loadingMyStoryStats: boolean;
  loadingFullStory: boolean;
  storyError?: ErrorResponse;
  allStoriesError?: ErrorResponse;
  followingStoriesError?: ErrorResponse;
  myStoriesError?: ErrorResponse;
  openShareStoryToRoomsDialog: boolean;
  shareStory?: Story;
  isStoryOffensive: boolean;
  editedFeaturedStory?: Story;
  editedFeaturedContent?: Story;
}

const initialState: StoriesState = {
  allStories: { data: [] },
  followingStories: { data: [] },
  myStories: { data: [] },
  suggestedStories: { data: [] },
  storiesByAuthor: { data: [] },
  storiesByTopic: { data: [] },
  titleTopicName: undefined,
  loadingStoriesByAuthor: false,
  loadingStoriesByTopic: false,
  storiesByAuthorDisplayName: undefined,
  storyTags: [],
  fullStory: undefined,
  storyTopics: [],
  editedStory: undefined,
  currentStorySettings: undefined,
  publishInProgress: false,
  loadingAllStories: false,
  loadingSuggestedStories: false,
  loadingFollowingStories: false,
  loadingMyStories: false,
  loadingStoryTags: false,
  loadingStoryTopics: false,
  myStoryStats: undefined,
  loadingMyStoryStats: false,
  loadingFullStory: false,
  openShareStoryToRoomsDialog: false,
  shareStory: undefined,
  isStoryOffensive: false,
  editedFeaturedStory: undefined,
  editedFeaturedContent: undefined,
};

// returns a list that only contains stories that aren't part of existingStoriesList
const deduplicateStories = (
  newStoriesList: Story[],
  existingStoriesList: Story[]
) => {
  const deduplicatedStories = (newStoriesList ?? []).filter(
    (story) =>
      existingStoriesList.filter((currentStory) => currentStory.id === story.id)
        .length === 0
  );
  return deduplicatedStories;
};

const modifyCommentCount = (
  stories: Story[],
  storyId: string,
  count: number
) => {
  const modifiedStory = stories.find((story) => story.id === storyId);
  if (modifiedStory) {
    modifiedStory.commentCount += count;
  }
};

const checkIfContentIsInappropriate = (
  action: PayloadAction<ErrorResponse | undefined>
) => {
  return action.payload?.status === HttpStatusCode.not_acceptable;
};

const storiesSlice = createSlice({
  name: 'stories',
  initialState,
  reducers: {
    clearFullStoryDetails: (state) => {
      state.fullStory = undefined;
      state.storyError = undefined;
    },
    clearMyStories: (state) => {
      state.myStories = {
        data: [],
        xMsContinuationToken: undefined,
      };
      state.myStoriesError = undefined;
    },
    clearFollowingStories: (state) => {
      state.followingStories = { data: [], xMsContinuationToken: undefined };
      state.followingStoriesError = undefined;
    },
    clearAllStories: (state) => {
      state.allStories = { data: [], xMsContinuationToken: undefined };
      state.allStoriesError = undefined;
    },
    clearRoomToNavigateBackTo: (state) => {
      if (state.currentStorySettings) {
        state.currentStorySettings.roomToReturnTo = undefined;
      }
    },
    setCurrentStorySettings: (state, action: PayloadAction<StorySettings>) => {
      state.currentStorySettings = action.payload;
    },
    clearCurrentStorySettings: (state) => {
      state.currentStorySettings = {};
      state.editedStory = undefined;
    },
    clearSuggestedStories: (state) => {
      state.suggestedStories = { data: [], xMsContinuationToken: undefined };
    },
    clearStoriesFromAuthor: (state) => {
      state.storiesByAuthor = { data: [], xMsContinuationToken: undefined };
    },
    clearStoriesByTopic: (state) => {
      state.storiesByTopic = { data: [], xMsContinuationToken: undefined };
      state.titleTopicName = undefined;
    },
    incrementDraftsCount: (state) => {
      if (state.myStoryStats) {
        state.myStoryStats.drafted++;
      } else {
        state.myStoryStats = { drafted: 1, published: 0 };
      }
    },
    incrementPublishedCount: (state) => {
      if (state.myStoryStats) {
        state.myStoryStats.drafted--;
        state.myStoryStats.published++;
      }
    },
    handleStatsOnUnpublishStory: (state) => {
      if (state.myStoryStats) {
        state.myStoryStats.drafted++;
        state.myStoryStats.published--;
      }
    },
    decrementCommentCountOnFullStory: (
      state,
      action: PayloadAction<number>
    ) => {
      if (state.fullStory?.commentCount) {
        state.fullStory.commentCount -= action.payload;
      }
    },
    saveEditedStoryLocally: (state, action: PayloadAction<InsertStoryDto>) => {
      if (state.editedStory) {
        state.editedStory.content = action.payload.content ?? '';
        state.editedStory.title = action.payload.title ?? '';
      }
    },
    setFullStory: (state, action: PayloadAction<Story>) => {
      state.fullStory = action.payload;
    },
    insertMemberStory: (state, action: PayloadAction<Story>) => {
      const currentStories = [...(state.myStories.data ?? [])];
      state.myStories = {
        data: [...currentStories, action.payload],
        xMsContinuationToken: state.myStories.xMsContinuationToken,
      };
    },
    setOpenShareStoryToRoomsDialog: (state, action: PayloadAction<boolean>) => {
      state.openShareStoryToRoomsDialog = action.payload;
    },
    setShareStory: (state, action: PayloadAction<Story | undefined>) => {
      state.shareStory = action.payload;
    },
    setIsStoryOffensive: (state, action: PayloadAction<boolean>) => {
      state.isStoryOffensive = action.payload;
    },
    setEditedFeaturedStory: (
      state,
      action: PayloadAction<Story | undefined>
    ) => {
      state.editedFeaturedStory = action.payload;
    },
    setEditedFeaturedContent: (
      state,
      action: PayloadAction<Story | undefined>
    ) => {
      state.editedFeaturedContent = action.payload;
    },
    setLoadingFullStory: (state, action: PayloadAction<boolean>) => {
      state.loadingFullStory = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllStories.pending, (state) => {
        state.loadingAllStories = true;
        state.allStoriesError = undefined;
      })
      .addCase(fetchAllStories.fulfilled, (state, action) => {
        // If no continuation token, this is a fresh fetch (refresh) - replace all data
        if (!action.meta.arg.xMsContinuationToken) {
          state.allStories = {
            data: [...action.payload.data],
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        } else {
          // If continuation token exists, this is pagination - append new data
          const stories = state.allStories?.data || [];
          const storiesToAdd = deduplicateStories(action.payload.data, stories);
          stories.push(...storiesToAdd);

          state.allStories = {
            data: stories,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        }
        state.loadingAllStories = false;
      })
      .addCase(updateFeaturedContent.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedStory = action.payload;
          state.editedFeaturedContent = updatedStory;
          if (state.fullStory && state.fullStory.id === updatedStory.id) {
            state.fullStory = updatedStory;
          }
        }
      })
      .addCase(fetchAllStories.rejected, (state, action) => {
        state.allStories = {
          data: [],
          xMsContinuationToken: undefined,
        };
        state.loadingAllStories = false;
        state.allStoriesError = action.payload;
      })

      .addCase(fetchSuggestedStories.pending, (state) => {
        state.loadingSuggestedStories = true;
      })
      .addCase(fetchSuggestedStories.fulfilled, (state, action) => {
        // If no continuation token, this is a fresh fetch (refresh) - replace all data
        if (!action.meta.arg.xMsContinuationToken) {
          state.suggestedStories = {
            data: action.payload.data,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        } else {
          // If continuation token exists, this is pagination - append new data
          const suggestedStories = state.suggestedStories?.data || [];
          const newSuggestedStories = deduplicateStories(
            action.payload.data,
            suggestedStories
          );
          suggestedStories.push(...newSuggestedStories);

          state.suggestedStories = {
            data: suggestedStories,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        }
        state.loadingSuggestedStories = false;
      })
      .addCase(fetchSuggestedStories.rejected, (state) => {
        state.suggestedStories = {
          data: [],
          xMsContinuationToken: undefined,
        };
        state.loadingSuggestedStories = false;
      })

      .addCase(fetchFollowingStories.pending, (state) => {
        state.loadingFollowingStories = true;
        state.followingStoriesError = undefined;
      })
      .addCase(fetchFollowingStories.fulfilled, (state, action) => {
        // If no continuation token, this is a fresh fetch (refresh) - replace all data
        if (!action.meta.arg.xMsContinuationToken) {
          state.followingStories = {
            data: action.payload.data,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        } else {
          // If continuation token exists, this is pagination - append new data
          const followingStories = state.followingStories?.data || [];
          const newStories = deduplicateStories(
            action.payload.data,
            followingStories
          );
          followingStories.push(...newStories);

          state.followingStories = {
            data: followingStories,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        }
        state.loadingFollowingStories = false;
      })
      .addCase(fetchFollowingStories.rejected, (state, action) => {
        state.followingStories = {
          data: [],
          xMsContinuationToken: undefined,
        };
        state.loadingFollowingStories = false;
        state.followingStoriesError = action.payload;
      })

      .addCase(fetchMyStories.pending, (state) => {
        state.loadingMyStories = true;
      })
      .addCase(fetchMyStories.rejected, (state, action) => {
        state.myStories = {
          data: [],
          xMsContinuationToken: undefined,
        };
        state.loadingMyStories = false;
        state.myStoriesError = action.payload;
      })
      .addCase(fetchMyStories.fulfilled, (state, action) => {
        // If no continuation token, this is a fresh fetch (refresh) - replace all data
        if (!action.meta.arg.xMsContinuationToken) {
          state.myStories = {
            data: action.payload.data,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        } else {
          // If continuation token exists, this is pagination - append new data
          const stories = state.myStories?.data || [];
          const storiesToAdd = deduplicateStories(action.payload.data, stories);
          stories.push(...storiesToAdd);

          state.myStories = {
            data: stories,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        }

        state.loadingMyStories = false;
        state.currentStorySettings = {};
        state.editedStory = undefined;
      })

      .addCase(fetchStoryTags.pending, (state) => {
        state.loadingStoryTags = true;
      })
      .addCase(fetchStoryTags.fulfilled, (state, action) => {
        state.storyTags = action.payload;
        state.loadingStoryTags = false;
      })
      .addCase(fetchStoryTags.rejected, (state) => {
        state.storyTags = [];
        state.loadingStoryTags = false;
      })

      .addCase(fetchStoryTopics.pending, (state) => {
        state.loadingStoryTopics = true;
      })
      .addCase(fetchStoryTopics.fulfilled, (state, action) => {
        state.storyTopics = action.payload;
        state.loadingStoryTopics = false;
      })
      .addCase(fetchStoryTopics.rejected, (state) => {
        state.storyTopics = [];
        state.loadingStoryTopics = false;
      })

      .addCase(fetchMyStoryStats.pending, (state, action) => {
        state.loadingMyStoryStats = true;
      })
      .addCase(fetchMyStoryStats.fulfilled, (state, action) => {
        state.myStoryStats = action.payload;
        state.loadingMyStoryStats = false;
      })
      .addCase(fetchMyStoryStats.rejected, (state) => {
        state.myStoryStats = undefined;
        state.loadingMyStoryStats = false;
      })
      .addCase(fetchFullStory.pending, (state) => {
        state.loadingFullStory = true;
        state.storyError = undefined;
      })
      .addCase(fetchFullStory.fulfilled, (state, action) => {
        state.fullStory = action.payload;
        state.loadingFullStory = false;
      })
      .addCase(fetchFullStory.rejected, (state, action) => {
        state.loadingFullStory = false;
        state.fullStory = undefined;
        state.storyError = action.payload;
      })
      .addCase(fetchFullStoryForEdit.pending, (state) => {
        state.loadingFullStory = true;
      })
      .addCase(fetchFullStoryForEdit.rejected, (state, action) => {
        state.loadingFullStory = false;
      })
      .addCase(fetchFullStoryForEdit.fulfilled, (state, action) => {
        if (
          state.editedStory?.state === MyStoryState.drafted ||
          !state.editedStory?.content
        ) {
          state.editedStory = action.payload;
        }
        state.currentStorySettings = {
          topics: action.payload?.topics.map((topic) => {
            return { topic: topic.topic, displayValue: topic.displayValue };
          }),
          tags:
            state.currentStorySettings?.tags ??
            action.payload?.tags.map((tag) => {
              return { tag: tag.tag };
            }),
          rooms: state.currentStorySettings?.rooms ?? action.payload?.rooms,
          allowComments: action.payload?.allowComments,
          allowLikes: action.payload?.allowLikes,
          allowShare: action.payload?.allowShare,
          roomToReturnTo: state.currentStorySettings?.roomToReturnTo,
        };
        state.loadingFullStory = false;
      })

      .addCase(updateFeaturedStory.pending, (state) => {
        state.publishInProgress = true;
      })
      .addCase(updateFeaturedStory.fulfilled, (state) => {
        state.publishInProgress = false;
      })
      .addCase(updateFeaturedStory.rejected, (state) => {
        state.publishInProgress = false;
      })

      .addCase(insertFeaturedStory.pending, (state) => {
        state.publishInProgress = true;
      })
      .addCase(insertFeaturedStory.fulfilled, (state) => {
        state.publishInProgress = false;
      })
      .addCase(insertFeaturedStory.rejected, (state) => {
        state.publishInProgress = false;
      })

      .addCase(insertStory.pending, (state) => {
        state.publishInProgress = true;
      })
      .addCase(insertStory.rejected, (state, action) => {
        state.isStoryOffensive = checkIfContentIsInappropriate(action);
        state.publishInProgress = false;
      })
      .addCase(insertStory.fulfilled, (state, action) => {
        state.editedStory = action.payload;
        state.publishInProgress = false;
        state.isStoryOffensive = false;

        if (
          state.myStories.data.filter((story) => story.id === action.payload.id)
            .length === 0
        ) {
          state.myStories = {
            data: [action.payload, ...state.myStories.data],
            xMsContinuationToken: state.myStories.xMsContinuationToken,
          };
        } else {
          // the story was a draft and now it was published
          const filteredStories = state.myStories.data.filter(
            (story) => story.id !== action.payload.id
          );
          state.myStories = {
            data: [action.payload, ...filteredStories],
            xMsContinuationToken: state.myStories.xMsContinuationToken,
          };
        }
      })

      .addCase(fetchStoriesByAuthor.pending, (state) => {
        state.loadingStoriesByAuthor = true;
      })
      .addCase(fetchStoriesByAuthor.rejected, (state) => {
        state.loadingStoriesByAuthor = false;
        state.storiesByAuthor = {
          data: [],
          xMsContinuationToken: undefined,
        };
      })
      .addCase(fetchStoriesByAuthor.fulfilled, (state, action) => {
        // If no continuation token, this is a fresh fetch (refresh) - replace all data
        if (!action.meta.arg.xMsContinuationToken) {
          state.storiesByAuthor = {
            data: action.payload.data,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        } else {
          // If continuation token exists, this is pagination - append new data
          const storiesByAuthor = state.storiesByAuthor?.data || [];
          const newStories = deduplicateStories(
            action.payload.data,
            storiesByAuthor
          );
          storiesByAuthor.push(...newStories);

          state.storiesByAuthor = {
            data: storiesByAuthor,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        }

        state.loadingStoriesByAuthor = false;
      })

      .addCase(fetchStoriesByTopic.pending, (state) => {
        state.loadingStoriesByTopic = true;
      })
      .addCase(fetchStoriesByTopic.rejected, (state) => {
        state.loadingStoriesByTopic = false;
        state.storiesByTopic = {
          data: [],
          xMsContinuationToken: undefined,
        };
      })
      .addCase(fetchStoriesByTopic.fulfilled, (state, action) => {
        state.titleTopicName = action.meta.arg.topicName;

        // If no continuation token, this is a fresh fetch (refresh) - replace all data
        if (!action.meta.arg.xMsContinuationToken) {
          state.storiesByTopic = {
            data: action.payload.data,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        } else {
          // If continuation token exists, this is pagination - append new data
          const storiesByTopic = state.storiesByTopic?.data || [];
          const newStories = deduplicateStories(
            action.payload.data,
            storiesByTopic
          );
          storiesByTopic.push(...newStories);

          state.storiesByTopic = {
            data: storiesByTopic,
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        }

        state.loadingStoriesByTopic = false;
      })

      .addCase(likeStory.fulfilled, (state, action) => {
        const storyCategory = action.meta.arg.storyCategory;
        const likedStory = action.payload;

        if (!storyCategory && state.fullStory) {
          state.fullStory = likedStory;
        }
        let index = state.allStories.data.findIndex(
          (story) => story.id === likedStory.id
        );
        state.allStories.data[index] = likedStory;

        index = state.followingStories.data.findIndex(
          (story) => story.id === likedStory.id
        );
        state.followingStories.data[index] = likedStory;

        index = state.myStories.data.findIndex(
          (story) => story.id === likedStory.id
        );
        state.myStories.data[index] = likedStory;

        index = state.storiesByAuthor.data.findIndex(
          (story) => story.id === likedStory.id
        );
        state.storiesByAuthor.data[index] = likedStory;

        index = state.suggestedStories.data.findIndex(
          (story) => story.id === likedStory.id
        );
        state.suggestedStories.data[index] = likedStory;

        index = state.storiesByTopic.data.findIndex(
          (story) => story.id === likedStory.id
        );
        state.storiesByTopic.data[index] = likedStory;
      })

      .addCase(deleteStory.fulfilled, (state, action) => {
        let filteredStories = state.myStories.data.filter(
          (story) => story.id !== action.meta.arg.storyId
        );
        state.myStories = {
          data: filteredStories,
          xMsContinuationToken: state.myStories.xMsContinuationToken,
        };

        filteredStories = state.allStories.data.filter(
          (story) => story.id !== action.meta.arg.storyId
        );
        state.allStories = {
          data: filteredStories,
          xMsContinuationToken: state.allStories.xMsContinuationToken,
        };

        filteredStories = state.followingStories.data.filter(
          (story) => story.id !== action.meta.arg.storyId
        );
        state.followingStories = {
          data: filteredStories,
          xMsContinuationToken: state.followingStories.xMsContinuationToken,
        };

        if (state.myStoryStats) {
          if (action.meta.arg.isDraft) {
            if (state.myStoryStats.drafted > 0) {
              state.myStoryStats.drafted--;
            }
          } else if (state.myStoryStats.published > 0) {
            state.myStoryStats.published--;
          }
        }
      })

      .addCase(moderatorDeleteStory.pending, (state, action) => {
        const handleFiltering = (listToFilter: Story[]) => {
          return listToFilter.filter(
            (story) => story.id !== action.meta.arg.storyId
          );
        };

        state.suggestedStories.data = handleFiltering(
          state.suggestedStories.data
        );
        state.storiesByAuthor.data = handleFiltering(
          state.storiesByAuthor.data
        );
        state.storiesByTopic.data = handleFiltering(state.storiesByTopic.data);
        state.allStories.data = handleFiltering(state.allStories.data);
        state.followingStories.data = handleFiltering(
          state.followingStories.data
        );
        state.myStories.data = handleFiltering(state.myStories.data);
      })
      .addCase(moderatorUpdateStory.pending, (state) => {
        state.publishInProgress = true;
      })
      .addCase(moderatorUpdateStory.rejected, (state, action) => {
        state.isStoryOffensive = checkIfContentIsInappropriate(action);
        state.publishInProgress = false;
      })
      .addCase(moderatorUpdateStory.fulfilled, (state, action) => {
        state.allStories.data = state.allStories.data.map((story) =>
          story.id === action.payload.id ? action.payload : story
        );
        state.publishInProgress = false;
      })

      .addCase(deleteCommentOnStory.fulfilled, (state, action) => {
        if (state.fullStory) {
          state.fullStory.commentCount--;
        }
        const storyId = action.meta.arg.storyId;
        modifyCommentCount(state.allStories.data, storyId, -1);
        modifyCommentCount(state.followingStories.data, storyId, -1);
        modifyCommentCount(state.myStories.data, storyId, -1);
        modifyCommentCount(state.suggestedStories.data, storyId, -1);
        modifyCommentCount(state.storiesByAuthor.data, storyId, -1);
        modifyCommentCount(state.storiesByTopic.data, storyId, -1);
      })

      .addMatcher(
        isAnyOf(
          insertCommentOnStory.fulfilled,
          insertReplyToCommentOnStory.fulfilled
        ),
        (state, action) => {
          if (state.fullStory) {
            state.fullStory.commentCount++;
          }
          const storyId = action.meta.arg.storyId;
          modifyCommentCount(state.allStories.data, storyId, 1);
          modifyCommentCount(state.followingStories.data, storyId, 1);
          modifyCommentCount(state.myStories.data, storyId, 1);
          modifyCommentCount(state.suggestedStories.data, storyId, 1);
          modifyCommentCount(state.storiesByAuthor.data, storyId, 1);
          modifyCommentCount(state.storiesByTopic.data, storyId, 1);
        }
      );
  },
});

export const {
  clearFullStoryDetails,
  setCurrentStorySettings,
  clearMyStories,
  clearFollowingStories,
  clearAllStories,
  clearCurrentStorySettings,
  clearSuggestedStories,
  clearRoomToNavigateBackTo,
  clearStoriesFromAuthor,
  clearStoriesByTopic,
  incrementDraftsCount,
  incrementPublishedCount,
  handleStatsOnUnpublishStory,
  decrementCommentCountOnFullStory,
  saveEditedStoryLocally,
  setFullStory,
  insertMemberStory,
  setOpenShareStoryToRoomsDialog,
  setShareStory,
  setIsStoryOffensive,
  setEditedFeaturedStory,
  setEditedFeaturedContent,
  setLoadingFullStory,
} = storiesSlice.actions;

export default storiesSlice.reducer;
